import { c as _c } from "react-compiler-runtime";
import { createContext, useContext, useMemo } from "react";
import { FieldValues, Path, useController } from "react-hook-form";
import { ErrorMessage, HintMessage, Label, useFieldIds } from "@/components/form";
import { MediaUploader, type MediaUploaderProps } from "@/components/ui/MediaUploader/MediaUploader";
type MediaUploadFieldContextValue = {
  inputId: string;
  labelId: string;
  hintId: string;
  errorId: string;
};
const MediaUploadFieldContext = createContext<MediaUploadFieldContextValue | null>(null);
function useMediaUploadFieldContext() {
  const ctx = useContext(MediaUploadFieldContext);
  if (!ctx) {
    throw new globalThis.Error("MediaUploadField components must be used within MediaUploadField.Root");
  }
  return ctx;
}
type MediaUploadLabelProps = {
  text: string;
  invalid?: boolean;
};
function MediaUploadLabel(t0) {
  const $ = _c(5);
  const {
    text,
    invalid
  } = t0;
  const {
    inputId,
    labelId
  } = useMediaUploadFieldContext();
  let t1;
  if ($[0] !== inputId || $[1] !== invalid || $[2] !== labelId || $[3] !== text) {
    t1 = <Label htmlFor={inputId} id={labelId} invalid={invalid} text={text} />;
    $[0] = inputId;
    $[1] = invalid;
    $[2] = labelId;
    $[3] = text;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
type MediaUploadHintProps = {
  text: string;
};
function MediaUploadHint(t0) {
  const $ = _c(3);
  const {
    text
  } = t0;
  const {
    hintId
  } = useMediaUploadFieldContext();
  let t1;
  if ($[0] !== hintId || $[1] !== text) {
    t1 = <HintMessage id={hintId} text={text} />;
    $[0] = hintId;
    $[1] = text;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
type MediaUploadErrorMessageProps = {
  text: string;
};
function MediaUploadErrorMessage(t0) {
  const $ = _c(4);
  const {
    text
  } = t0;
  const {
    inputId,
    errorId
  } = useMediaUploadFieldContext();
  let t1;
  if ($[0] !== errorId || $[1] !== inputId || $[2] !== text) {
    t1 = <ErrorMessage htmlFor={inputId} id={errorId} text={text} />;
    $[0] = errorId;
    $[1] = inputId;
    $[2] = text;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  return t1;
}
type RootProps = {
  children: React.ReactNode;
  name: string;
};
function Root(t0) {
  const $ = _c(10);
  const {
    children,
    name
  } = t0;
  const fieldIds = useFieldIds(name);
  let t1;
  if ($[0] !== fieldIds.errorId || $[1] !== fieldIds.hintId || $[2] !== fieldIds.inputId || $[3] !== fieldIds.labelId) {
    t1 = {
      inputId: fieldIds.inputId,
      labelId: fieldIds.labelId,
      hintId: fieldIds.hintId,
      errorId: fieldIds.errorId
    };
    $[0] = fieldIds.errorId;
    $[1] = fieldIds.hintId;
    $[2] = fieldIds.inputId;
    $[3] = fieldIds.labelId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const value = t1;
  let t2;
  if ($[5] !== children) {
    t2 = <div className="flex flex-col gap-1 text-left">{children}</div>;
    $[5] = children;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2 || $[8] !== value) {
    t3 = <MediaUploadFieldContext.Provider value={value}>{t2}</MediaUploadFieldContext.Provider>;
    $[7] = t2;
    $[8] = value;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
}
type UploaderPassthroughProps = Omit<MediaUploaderProps, "onFileSelected" | "onReset" | "thumbnailUrl" | "status" | "errorMessage">;
type NativeProps = UploaderPassthroughProps & {
  name: string;
  label: string;
  hint?: string;
  value?: string | null;
  onFileSelected: (file: File) => void;
  onReset?: () => void;
  thumbnailUrl?: string | null;
  status?: MediaUploaderProps["status"];
  errorMessage?: string | null;
};
function Native(t0) {
  const $ = _c(26);
  let errorMessage;
  let hint;
  let label;
  let name;
  let onFileSelected;
  let onReset;
  let status;
  let thumbnailUrl;
  let uploaderProps;
  if ($[0] !== t0) {
    const {
      name: t1,
      label: t2,
      hint: t3,
      value: _value,
      onFileSelected: t4,
      onReset: t5,
      thumbnailUrl: t6,
      status: t7,
      errorMessage: t8,
      ...t9
    } = t0;
    name = t1;
    label = t2;
    hint = t3;
    onFileSelected = t4;
    onReset = t5;
    thumbnailUrl = t6;
    status = t7;
    errorMessage = t8;
    uploaderProps = t9;
    $[0] = t0;
    $[1] = errorMessage;
    $[2] = hint;
    $[3] = label;
    $[4] = name;
    $[5] = onFileSelected;
    $[6] = onReset;
    $[7] = status;
    $[8] = thumbnailUrl;
    $[9] = uploaderProps;
  } else {
    errorMessage = $[1];
    hint = $[2];
    label = $[3];
    name = $[4];
    onFileSelected = $[5];
    onReset = $[6];
    status = $[7];
    thumbnailUrl = $[8];
    uploaderProps = $[9];
  }
  let t1;
  if ($[10] !== label) {
    t1 = <MediaUploadLabel text={label} />;
    $[10] = label;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== errorMessage || $[13] !== onFileSelected || $[14] !== onReset || $[15] !== status || $[16] !== thumbnailUrl || $[17] !== uploaderProps) {
    t2 = <MediaUploader {...uploaderProps} errorMessage={errorMessage} status={status} thumbnailUrl={thumbnailUrl} onFileSelected={onFileSelected} onReset={onReset} />;
    $[12] = errorMessage;
    $[13] = onFileSelected;
    $[14] = onReset;
    $[15] = status;
    $[16] = thumbnailUrl;
    $[17] = uploaderProps;
    $[18] = t2;
  } else {
    t2 = $[18];
  }
  let t3;
  if ($[19] !== hint) {
    t3 = hint && <MediaUploadHint text={hint} />;
    $[19] = hint;
    $[20] = t3;
  } else {
    t3 = $[20];
  }
  let t4;
  if ($[21] !== name || $[22] !== t1 || $[23] !== t2 || $[24] !== t3) {
    t4 = <Root name={name}>{t1}{t2}{t3}</Root>;
    $[21] = name;
    $[22] = t1;
    $[23] = t2;
    $[24] = t3;
    $[25] = t4;
  } else {
    t4 = $[25];
  }
  return t4;
}
type RHFProps<T extends FieldValues> = UploaderPassthroughProps & {
  name: Path<T>;
  label: string;
  hint?: string;
  onFileSelected: (file: File) => void;
  thumbnailUrl?: string | null;
  status?: MediaUploaderProps["status"];
  errorMessage?: string | null;
};
function RHF(t0) {
  const $ = _c(36);
  let errorMessage;
  let hint;
  let label;
  let name;
  let onFileSelected;
  let status;
  let thumbnailUrl;
  let uploaderProps;
  if ($[0] !== t0) {
    ({
      name,
      label,
      hint,
      onFileSelected,
      thumbnailUrl,
      status,
      errorMessage,
      ...uploaderProps
    } = t0);
    $[0] = t0;
    $[1] = errorMessage;
    $[2] = hint;
    $[3] = label;
    $[4] = name;
    $[5] = onFileSelected;
    $[6] = status;
    $[7] = thumbnailUrl;
    $[8] = uploaderProps;
  } else {
    errorMessage = $[1];
    hint = $[2];
    label = $[3];
    name = $[4];
    onFileSelected = $[5];
    status = $[6];
    thumbnailUrl = $[7];
    uploaderProps = $[8];
  }
  let t1;
  if ($[9] !== name) {
    t1 = {
      name
    };
    $[9] = name;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  const {
    field,
    fieldState
  } = useController(t1);
  let t2;
  if ($[11] !== field) {
    t2 = () => {
      field.onChange(null);
    };
    $[11] = field;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const handleReset = t2;
  const errorText = fieldState.error?.message ?? (status === "error" ? errorMessage : undefined);
  const t3 = !!errorText;
  let t4;
  if ($[13] !== label || $[14] !== t3) {
    t4 = <MediaUploadLabel invalid={t3} text={label} />;
    $[13] = label;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const t5 = errorText ? "error" : status;
  const t6 = thumbnailUrl ?? (typeof field.value === "string" ? field.value : null);
  let t7;
  if ($[16] !== errorText || $[17] !== handleReset || $[18] !== onFileSelected || $[19] !== t5 || $[20] !== t6 || $[21] !== uploaderProps) {
    t7 = <MediaUploader {...uploaderProps} errorMessage={errorText} status={t5} thumbnailUrl={t6} onFileSelected={onFileSelected} onReset={handleReset} />;
    $[16] = errorText;
    $[17] = handleReset;
    $[18] = onFileSelected;
    $[19] = t5;
    $[20] = t6;
    $[21] = uploaderProps;
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  let t8;
  if ($[23] !== errorText || $[24] !== hint) {
    t8 = hint && !errorText && <MediaUploadHint text={hint} />;
    $[23] = errorText;
    $[24] = hint;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== errorText) {
    t9 = errorText && <MediaUploadErrorMessage text={errorText} />;
    $[26] = errorText;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== t9) {
    t10 = <div className="h-2">{t9}</div>;
    $[28] = t9;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== name || $[31] !== t10 || $[32] !== t4 || $[33] !== t7 || $[34] !== t8) {
    t11 = <Root name={name}>{t4}{t7}{t8}{t10}</Root>;
    $[30] = name;
    $[31] = t10;
    $[32] = t4;
    $[33] = t7;
    $[34] = t8;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
}
export const MediaUploadField = {
  RHF,
  Native
};